import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Sidebar } from '@/components/Sidebar';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { fetchWithAuth } from '@/utils/api';
import { 
  Shield,
  ArrowUpDown,
  Calendar,
  FileText,
  DollarSign,
  Wallet,
  CheckCircle2,
  Circle,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Package,
  ArrowRight,
  ShieldAlert
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHelpButton from '@/components/PageHelpButton';
import EducationalPanel from '@/components/EducationalPanel';
import { toast } from 'sonner';
import { showError } from '../utils/errors';
import { format, parseISO, subDays } from 'date-fns';
import { safeFormatDate } from '@/utils/safeDate';

export default function GovernancePage() {
  const { selectedTrust } = useAuth();
  const [governance, setGovernance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedTrust) {
      loadGovernanceData();
    }
  }, [selectedTrust]);

  const loadGovernanceData = async () => {
    if (!selectedTrust) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [govResponse, histResponse] = await Promise.all([
        fetchWithAuth(`/governance/${selectedTrust.trust_id}`),
        fetchWithAuth(`/governance/${selectedTrust.trust_id}/history?days=30`)
      ]);
      
      if (govResponse.ok) {
        setGovernance(await govResponse.json());
      }
      if (histResponse.ok) {
        setHistory((await histResponse.json()).history || []);
      }
    } catch (error) {
      console.error('Failed to load governance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 96) return 'text-success';
    if (score >= 72) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score) => {
    if (score >= 96) return 'bg-success';
    if (score >= 72) return 'bg-warning';
    return 'bg-error';
  };

  const getCriterionIcon = (name) => {
    switch (name) {
      case 'Quarterly Minutes': return <FileText className="w-5 h-5" />;
      case 'Task Compliance': return <Calendar className="w-5 h-5" />;
      case 'Compensation Alignment': return <Wallet className="w-5 h-5" />;
      case 'Distribution Documentation': return <DollarSign className="w-5 h-5" />;
      case 'Annual Review': return <TrendingUp className="w-5 h-5" />;
      case 'Asset Valuation Freshness': return <Package className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM d');
    } catch {
      return dateString;
    }
  };

  // Calculate chart dimensions
  const chartHeight = 120;
  const chartWidth = 100;
  const maxScore = governance?.max_score || 115;

  // Generate chart points
  const getChartPoints = () => {
    if (history.length === 0) return '';
    
    const points = history.map((item, index) => {
      const x = (index / (history.length - 1 || 1)) * chartWidth;
      const y = chartHeight - (item.score / maxScore) * chartHeight;
      return `${x},${y}`;
    });
    
    return points.join(' ');
  };

  const getChartArea = () => {
    if (history.length === 0) return '';
    
    const points = history.map((item, index) => {
      const x = (index / (history.length - 1 || 1)) * chartWidth;
      const y = chartHeight - (item.score / maxScore) * chartHeight;
      return `${x},${y}`;
    });
    
    return `0,${chartHeight} ${points.join(' ')} ${chartWidth},${chartHeight}`;
  };

  // Calculate trend
  const getTrend = () => {
    if (history.length < 2) return { direction: 'stable', change: 0 };
    const first = history[0].score;
    const last = history[history.length - 1].score;
    const change = last - first;
    return {
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
      change: Math.abs(change)
    };
  };

  const trend = getTrend();

  return (
    <div className="main-layout" data-testid="governance-page">
      <Sidebar />
      <main className="main-content dot-grid">
        <div className="page-container">
          {/* Page Header */}
          <div className="page-header flex items-center justify-between">
            <div>
              <h1 className="page-title">Trust Health</h1>
              <p className="page-subtitle">
                Assess trust health across 8 criteria — track defensibility, compliance, and overall governance quality
              </p>
            </div>
            <div className="flex items-center gap-2">
              <PageHelpButton
                items={[
                  { text: 'Assess trust health across 8 criteria including defensibility and compliance' },
                  { text: 'Track your trust governance quality score over time' },
                  { text: 'Identify areas that need attention to improve your trust health' },
                ]}
                taPrompt="Walk me through the Trust Health page and how to interpret my scores"
              />
              <Button 
                onClick={loadGovernanceData}
                variant="outline"
                className="btn-secondary"
                data-testid="refresh-score-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 card-trust">
                <div className="skeleton h-48 w-full"></div>
              </div>
              <div className="card-trust">
                <div className="skeleton h-48 w-full"></div>
              </div>
            </div>
          ) : (
            <>
              {/* Score Overview + Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Main Score */}
                <div className="lg:col-span-2 card-trust corner-mark">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Score Circle */}
                    <div className="text-center">
                      <div className="score-circle">
                        <span className={`score-indicator ${getScoreColor(governance?.total_score || 0)}`}>
                          {governance?.total_score || 0}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                          / 115
                        </span>
                      </div>
                      <div className="mt-4 flex items-center justify-center gap-2">
                        {trend.direction === 'up' ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : trend.direction === 'down' ? (
                          <TrendingDown className="w-4 h-4 text-error" />
                        ) : null}
                        <span className={`font-mono text-sm ${
                          trend.direction === 'up' ? 'text-success' : 
                          trend.direction === 'down' ? 'text-error' : 'text-muted-foreground'
                        }`}>
                          {trend.direction === 'stable' ? 'Stable' : 
                           `${trend.direction === 'up' ? '+' : '-'}${trend.change} pts (30d)`}
                        </span>
                      </div>
                      {governance?.risk_penalty < 0 && (
                        <div className="text-xs text-muted-foreground mt-2 text-center">
                          Governance: {governance?.base_score}/{governance?.max_score || 115}
                          {governance?.has_critical_risk && (
                            <span className="text-error font-medium block mt-1">
                              Critical risk found, score capped at 50
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Criteria List */}
                    <div className="flex-1 w-full">
                      <h3 className="font-serif text-lg text-navy mb-4">Governance Score</h3>
                      <div className="space-y-3">
                        {governance?.criteria?.map((criterion, index) => (
                          <div 
                            key={index}
                            className={`flex items-center justify-between p-3 border ${
                              criterion.achieved ? 'border-success/30 bg-success/5' : 'border-navy/10'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {criterion.achieved ? (
                                <CheckCircle2 className="w-5 h-5 text-success" />
                              ) : (
                                <Circle className="w-5 h-5 text-navy/30" />
                              )}
                              <div className={`w-8 h-8 flex items-center justify-center ${
                                criterion.achieved ? 'bg-success/20 text-success' : 'bg-navy/10 text-navy/50'
                              }`}>
                                {getCriterionIcon(criterion.name)}
                              </div>
                              <div>
                                <p className={`font-medium text-sm ${criterion.achieved ? 'text-navy' : 'text-muted-foreground'}`}>
                                  {criterion.name}
                                </p>
                                <p className="text-xs text-muted-foreground">{criterion.description}</p>
                              </div>
                            </div>
                            <span className={`font-mono text-lg ${criterion.achieved ? 'text-success' : 'text-muted-foreground'}`}>
                              {criterion.points}/{criterion.max_points}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Historical Chart */}
                <div className="card-trust">
                  <h3 className="font-serif text-lg text-navy mb-4">30-Day Trend</h3>
                  
                  {history.length === 0 ? (
                    <div className="text-center py-8">
                      <TrendingUp className="w-8 h-8 text-navy/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No historical data yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Check back after a few days</p>
                    </div>
                  ) : (
                    <>
                      <div className="relative h-32 mb-4" data-testid="health-chart">
                        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-full" preserveAspectRatio="none">
                          {/* Grid lines */}
                          <line x1="0" y1={chartHeight * 0.2} x2={chartWidth} y2={chartHeight * 0.2} stroke="#e5e7eb" strokeWidth="0.5" />
                          <line x1="0" y1={chartHeight * 0.4} x2={chartWidth} y2={chartHeight * 0.4} stroke="#e5e7eb" strokeWidth="0.5" />
                          <line x1="0" y1={chartHeight * 0.6} x2={chartWidth} y2={chartHeight * 0.6} stroke="#e5e7eb" strokeWidth="0.5" />
                          <line x1="0" y1={chartHeight * 0.8} x2={chartWidth} y2={chartHeight * 0.8} stroke="#e5e7eb" strokeWidth="0.5" />
                          
                          {/* Area fill */}
                          <polygon 
                            points={getChartArea()} 
                            fill="url(#gradient)" 
                            opacity="0.3"
                          />
                          
                          {/* Line */}
                          <polyline 
                            points={getChartPoints()} 
                            fill="none" 
                            stroke="#010079" 
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          
                          {/* Gradient definition */}
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="#010079" stopOpacity="0.3" />
                              <stop offset="100%" stopColor="#010079" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          
                          {/* Data points */}
                          {history.map((item, index) => {
                            const x = (index / (history.length - 1 || 1)) * chartWidth;
                            const y = chartHeight - (item.score / maxScore) * chartHeight;
                            return (
                              <circle 
                                key={index}
                                cx={x} 
                                cy={y} 
                                r="3" 
                                fill="#010079"
                              />
                            );
                          })}
                        </svg>
                      </div>
                      
                      {/* Date labels */}
                      <div className="flex justify-between text-xs text-muted-foreground font-mono">
                        {history.length > 0 && (
                          <>
                            <span>{formatDate(history[0]?.date)}</span>
                            {history.length > 2 && <span>{formatDate(history[Math.floor(history.length / 2)]?.date)}</span>}
                            <span>{formatDate(history[history.length - 1]?.date)}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Score labels */}
                      <div className="absolute right-2 top-8 text-[10px] text-muted-foreground font-mono flex flex-col justify-between h-32">
                        <span>115</span>
                        <span>60</span>
                        <span>0</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Risk Overview */}
              {governance?.criteria && (() => {
                const atRisk = governance.criteria.filter(c => !c.achieved && !c.no_data);
                const missingPoints = governance.criteria.reduce((sum, c) => sum + ((c.max_points || 15) - c.points), 0);
                const criticalCount = atRisk.filter(c => c.points === 0).length;
                const partialCount = atRisk.filter(c => c.points > 0 && c.points < (c.max_points || 15)).length;

                if (atRisk.length === 0) {
                  return (
                    <div className="card-trust mb-8 border-l-4 border-l-success">
                      <div className="flex items-center gap-3 p-4">
                        <CheckCircle2 className="w-6 h-6 text-success flex-shrink-0" />
                        <div>
                          <h3 className="font-serif text-lg text-navy">Risk Overview</h3>
                          <p className="text-sm text-success">
                            All criteria are met. Your trust is in good standing across all 8 areas.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="card-trust mb-8" data-testid="risk-overview">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-error/10 flex items-center justify-center">
                          <ShieldAlert className="w-5 h-5 text-error" />
                        </div>
                        <div>
                          <h3 className="font-serif text-lg text-navy">Risk Overview</h3>
                          <p className="text-sm text-muted-foreground">
                            {missingPoints} points missing across {atRisk.length} area{atRisk.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {criticalCount > 0 && (
                          <div className="text-center">
                            <p className="font-mono text-2xl text-error">{criticalCount}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Critical</p>
                          </div>
                        )}
                        {partialCount > 0 && (
                          <div className="text-center">
                            <p className="font-mono text-2xl text-warning">{partialCount}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Partial</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {atRisk
                        .sort((a, b) => ((b.max_points || 15) - b.points) - ((a.max_points || 15) - a.points))
                        .map((criterion, index) => {
                          const pointsLost = (criterion.max_points || 15) - criterion.points;
                          const isCritical = criterion.points === 0;
                          return (
                            <div
                              key={index}
                              className={`flex items-center justify-between p-3 border ${
                                isCritical ? 'border-error/20 bg-error/5' : 'border-warning/20 bg-warning/5'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {isCritical ? (
                                  <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
                                ) : (
                                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0" />
                                )}
                                <div>
                                  <p className={`font-medium text-sm ${isCritical ? 'text-navy' : 'text-navy'}`}>
                                    {criterion.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{criterion.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className={`font-mono text-sm ${isCritical ? 'text-error' : 'text-warning'}`}>
                                  -{pointsLost} pts
                                </span>
                                <span className="font-mono text-xs text-muted-foreground">
                                  {criterion.points}/{criterion.max_points || 15}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    <div className="mt-4 pt-4 border-t border-navy/10">
                      <Link
                        to="/risk"
                        className="text-navy hover:text-navy/70 font-mono text-xs uppercase tracking-widest flex items-center gap-1"
                      >
                        View Full Risk Dashboard <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                );
              })()}

              {/* Areas Needing Attention (Risk Findings) */}
              {governance?.risk_findings?.length > 0 && (
                <div className="card-trust mb-8" data-testid="risk-findings-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-error/10 flex items-center justify-center">
                        <ShieldAlert className="w-5 h-5 text-error" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg text-navy">Areas Needing Attention</h3>
                        <p className="text-sm text-muted-foreground">
                          These items from your Risk Dashboard are affecting your score.
                          Resolve them to recover points.
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg text-error">{governance.risk_penalty}</p>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Penalty</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {governance.risk_findings
                      .sort((a, b) => {
                        const sevOrder = { critical: 0, high: 1, medium: 2, low: 3 };
                        return (sevOrder[a.severity] ?? 4) - (sevOrder[b.severity] ?? 4);
                      })
                      .map((risk, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between p-3 border ${
                          risk.severity === 'critical' ? 'border-error/20 bg-error/5' :
                          risk.severity === 'high' ? 'border-error/10 bg-error/5' :
                          risk.severity === 'medium' ? 'border-warning/20 bg-warning/5' :
                          'border-navy/10'
                        }`}
                        data-testid={`risk-finding-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-lg font-mono ${
                            risk.severity === 'critical' ? 'text-error' :
                            risk.severity === 'high' ? 'text-error' :
                            risk.severity === 'medium' ? 'text-warning' :
                            'text-muted-foreground'
                          }`}>
                            {risk.severity === 'critical' || risk.severity === 'high' ? '!' : '\u2022'}
                          </span>
                          <div>
                            <p className="text-sm text-navy font-medium">{risk.title}</p>
                            <p className="text-xs text-muted-foreground">{risk.detail}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="font-mono text-xs text-error">
                            {risk.penalty || 0} pts
                          </span>
                          <Link
                            to={risk.deeplink}
                            className="text-gold text-sm hover:underline font-mono uppercase tracking-wider"
                          >
                            Resolve
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-navy/10">
                    <Link
                      to="/risk"
                      className="text-navy hover:text-navy/70 font-mono text-xs uppercase tracking-widest flex items-center gap-1"
                    >
                      View Full Risk Dashboard <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              )}

              {/* Scoring Guide */}
              <div className="card-trust">
                <h3 className="font-serif text-xl text-navy mb-6">How Scoring Works</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Quarterly Minutes</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Generate meeting minutes each quarter
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Task Compliance</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Complete governance tasks before due dates
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Compensation</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Stay within approved plan amounts
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Distributions</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Log and fully document distributions
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Annual Review</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Complete annual trust review
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Asset Valuation Freshness</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Keep Trust Assets valuations current (within 12 months)
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Transaction Classification</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Classify all trust transactions by category
                    </p>
                    <span className="badge-trust">10 points</span>
                  </div>

                  <div className="p-4 border border-navy/10">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldAlert className="w-5 h-5 text-navy" />
                      <h4 className="font-medium text-sm">Separation Alert Health</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      No active separation of trust assets detected
                    </p>
                    <span className="badge-trust">15 points</span>
                  </div>
                </div>

                {/* Risk Penalty explanation */}
                <div className="mt-6 pt-6 border-t border-navy/10">
                  <p className="label-trust mb-3">Risk Adjustments</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Your governance score is adjusted by risk findings from your Risk Dashboard.
                    Penalties are capped by severity tier, and critical findings impose a hard floor of 50.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 border border-error/20 bg-error/5 text-center">
                      <p className="font-mono text-sm text-error">-20 pts</p>
                      <p className="text-xs text-muted-foreground">Critical (per finding)</p>
                    </div>
                    <div className="p-2 border border-error/10 bg-error/5 text-center">
                      <p className="font-mono text-sm text-error">-12 pts</p>
                      <p className="text-xs text-muted-foreground">High (capped at -25)</p>
                    </div>
                    <div className="p-2 border border-warning/20 bg-warning/5 text-center">
                      <p className="font-mono text-sm text-warning">-5 pts</p>
                      <p className="text-xs text-muted-foreground">Medium (capped at -15)</p>
                    </div>
                    <div className="p-2 border border-navy/10 text-center">
                      <p className="font-mono text-sm text-muted-foreground">-2 pts</p>
                      <p className="text-xs text-muted-foreground">Low (capped at -10)</p>
                    </div>
                  </div>
                </div>

                {/* Color coding guide */}
                <div className="mt-6 pt-6 border-t border-navy/10">
                  <p className="label-trust mb-3">Score Ranges</p>
                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-success"></div>
                      <span className="text-sm">96-115: Excellent</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-warning"></div>
                      <span className="text-sm">72-95: Needs Attention</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-error"></div>
                      <span className="text-sm">Below 72: Critical</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Hidden Insights — Manually Restore */}
              <div className="card-trust mt-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-xl text-navy">Hidden Recommendations</h3>
                    <p className="text-sm text-muted-foreground">
                      Insights you've dismissed. Restore them to bring them back.
                    </p>
                  </div>
                </div>
                <HiddenInsightsPanel trustId={selectedTrust?.trust_id} />
              </div>

              {/* Educational Resources */}
              <div className="mt-8">
                <EducationalPanel trustId={selectedTrust?.trust_id} healthScore={governance?.total_score} />
              </div>
            </>
          )}
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
}

// Sub-component: Hidden Insights Panel (self-contained)
function HiddenInsightsPanel({ trustId }) {
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (trustId) loadDismissed();
  }, [trustId]);

  const loadDismissed = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`/insights/dismissed?trust_id=${trustId}`);
      if (response.ok) {
        const data = await response.json();
        setDismissed(data.dismissed_insights || []);
      }
    } catch (error) {
      console.error('Failed to load dismissed insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const restoreInsight = async (criterionName) => {
    try {
      const response = await fetchWithAuth('/insights/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trust_id: trustId, criterion_name: criterionName })
      });
      if (response.ok) {
        toast.success(`${criterionName} restored`);
        setDismissed(prev => prev.filter(d => d.criterion_name !== criterionName));
      } else {
        const data = await response.json();
        showError(toast, new Error(data.detail || 'Failed to restore'), { operation: 'restore', page: 'Governance' });
      }
    } catch (error) {
      console.error('Failed to restore insight:', error);
      showError(toast, error, { operation: 'restore', page: 'Governance' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-5 h-5 animate-spin text-gold mr-2" />
        <span className="text-sm text-muted-foreground">Loading hidden recommendations...</span>
      </div>
    );
  }

  if (dismissed.length === 0) {
    return (
      <div className="p-4 bg-success/10 border border-success/20 text-center">
        <p className="text-sm text-success">
          No hidden recommendations. Everything is visible.
        </p>
      </div>
    );
  }

  const INSIGHT_ICONS = {
    'Quarterly Minutes': FileText,
    'Task Compliance': Calendar,
    'Compensation Alignment': Wallet,
    'Distribution Documentation': DollarSign,
    'Annual Review': TrendingUp
  };

  return (
    <div className="space-y-3">
      {dismissed.map((item, index) => {
        const Icon = INSIGHT_ICONS[item.criterion_name] || Shield;
        return (
          <div
            key={index}
            className="flex items-center justify-between p-4 border border-navy/10 bg-navy/5"
            data-testid={`hidden-insight-${index}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-navy/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-navy" />
              </div>
              <div>
                <p className="font-medium text-sm text-navy">{item.criterion_name}</p>
                <p className="text-xs text-muted-foreground">
                  Dismissed {safeFormatDate(item.dismissed_at, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            <Button
              onClick={() => restoreInsight(item.criterion_name)}
              size="sm"
              variant="outline"
              className="btn-secondary"
              data-testid={`restore-insight-${index}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Restore
            </Button>
          </div>
        );
      })}
    </div>
  );
}
